<template>
  <header class="global-header">
    <GlobalHeaderPc
      v-if="!isSp"
      :is-logged-in="!isLoading && computedIsLoggedIn"
      :user-name="computedUserName"
      :nav-items="isLoading ? [] : navItemsWithBadge"
      :settings-menu-items="isLoading ? [] : settingsMenuItemsWithResumeGenerator"
      :is-active="isActive"
      :on-logout="handleLogout"
      :is-loading="isLoading"
      :platform="computedPlatform"
      @open-direct-modal="openDirectModal"
      @open-agent-service-modal="openAgentServiceGuideModal"
    />
    <GlobalHeaderSp
      v-else
      :is-logged-in="!isLoading && computedIsLoggedIn"
      :user-name="computedUserName"
      :is-active="isActive"
      :go-search="goSearch"
      :on-logout="handleLogout"
      :is-loading="isLoading"
      :settings-menu-items="isLoading ? [] : settingsMenuItemsWithResumeGenerator"
      :consideration-badge-count="considerationBadgeCount"
      :scout-badge-count="scoutBadgeCount"
      :selection-badge-count="selectionBadgeCount"
      :platform="computedPlatform"
    />
    <PlatformDirectModal v-model="isDirectModalOpen" />
    <AgentServiceGuideModal v-model="isAgentServiceGuideModalOpen" />
  </header>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import { getCareerDocumentAvailability } from '@/apis/career-documents.api';
import AgentServiceGuideModal from '@/components/shared/modal/AgentServiceGuideModal.vue';
import PlatformDirectModal from '@/components/shared/modal/PlatformDirectModal.vue';
import { AGENT_SEARCH_PATH } from '@/constant/routers/path';
import { VIEWPORT_WIDTH_SP_MAX } from '@/constant/scroll';
import router from '@/routers';
import { appAuthPresentation } from '@/utils/authMe';
import { isCandidateApiCodeSuccess } from '@/utils/candidate/isCandidateApiCodeSuccess';
import { HEADER_BADGE_REFRESH_EVENT, type HeaderBadgeRefreshDetail } from '@/utils/headerBadgeSync';
import {
  RESUME_GENERATOR_AVAILABILITY_REFRESH_EVENT,
  requestResumeGeneratorAvailabilityRefresh,
} from '@/utils/resume-generator/headerAvailabilitySync';
import { isClientSideSpaShellPresent } from '@/utils/spaNavigation';
import { toBadgeCount } from '@/utils/toBadgeCount';
import GlobalHeaderPc from './GlobalHeaderPc.vue';
import GlobalHeaderSp from './GlobalHeaderSp.vue';
import { detectActiveNav, navItems, settingsMenuItems } from './navData';
import type { GlobalHeaderProps, NavItem } from './types.d';

const props = defineProps<GlobalHeaderProps>();

const activeNavLabel = ref<string>('マイページ');
const isSp = ref(false);
const computedIsLoggedIn = ref<boolean>(!!props.isLoggedIn);
const computedUserName = ref<string>(props.userName ?? '');
const computedPlatform = ref<string>('');
const considerationBadgeCount = ref<number | undefined>(props.considerationBadgeCount);
const scoutBadgeCount = ref<number | undefined>(props.scoutBadgeCount);
const selectionBadgeCount = ref<number | undefined>(props.selectionBadgeCount);
const isLoading = ref<boolean>(props.isLoggedIn ? false : true);
const isDirectModalOpen = ref(false);
const isAgentServiceGuideModalOpen = ref(false);
const isResumeGeneratorAvailable = ref(false);

const updateIsSp = () => {
  isSp.value = window.innerWidth <= VIEWPORT_WIDTH_SP_MAX;
};

const isActive = (item: NavItem) => activeNavLabel.value === item.label;

const runDetectActive = () => {
  const { pathname, search } = window.location;
  activeNavLabel.value = detectActiveNav(pathname, search);
};

const goSearch = () => {
  if (isClientSideSpaShellPresent()) {
    router.push({ path: AGENT_SEARCH_PATH });
  } else {
    window.location.assign(AGENT_SEARCH_PATH);
  }
};

const applyHeaderBadgesFromDetail = (detail: HeaderBadgeRefreshDetail): boolean => {
  const p = detail as Record<string, unknown>;
  const hasBadgeKey =
    p.consideration_badge_count !== undefined ||
    p.considerationBadgeCount !== undefined ||
    p.scout_badge_count !== undefined ||
    p.scoutBadgeCount !== undefined ||
    p.selection_badge_count !== undefined ||
    p.selectionBadgeCount !== undefined;
  if (!hasBadgeKey) return false;
  considerationBadgeCount.value = toBadgeCount(
    p.consideration_badge_count ?? p.considerationBadgeCount
  );
  scoutBadgeCount.value = toBadgeCount(p.scout_badge_count ?? p.scoutBadgeCount);
  selectionBadgeCount.value = toBadgeCount(p.selection_badge_count ?? p.selectionBadgeCount);
  return true;
};

const openDirectModal = (): void => {
  isDirectModalOpen.value = true;
};

const openAgentServiceGuideModal = (): void => {
  isAgentServiceGuideModalOpen.value = true;
};

const tryFetchCareerDocumentAvailability = async (): Promise<void> => {
  try {
    const response = await getCareerDocumentAvailability();
    isResumeGeneratorAvailable.value =
      isCandidateApiCodeSuccess(response) && response.data?.available === true;
  } catch (error) {
    console.error('[GlobalHeader] Failed to fetch career document availability', error);
    isResumeGeneratorAvailable.value = false;
  }
};

const tryFetchProfile = async () => {
  try {
    const response = await fetch('/v1/candidates/profile', { credentials: 'include' });
    if (!response.ok) return;
    const data = await response.json().catch(() => ({}));
    const payload = data?.data || data || {};
    const profile = payload?.candidate || payload?.profile || payload || {};
    if (profile.platform) {
      computedPlatform.value = String(profile.platform).toLowerCase();
    }
    applyHeaderBadgesFromDetail(profile as HeaderBadgeRefreshDetail);
    const nameCandidate =
      profile.full_name ||
      profile.name ||
      (profile.last_name && profile.first_name ? `${profile.last_name} ${profile.first_name}` : '');
    if (nameCandidate) {
      computedUserName.value = nameCandidate as string;
    }
  } catch (error) {
    console.error('[GlobalHeader] Failed to fetch profile', error);
  } finally {
    isLoading.value = false;
  }
};

/** ルーターが `syncAppAuthClass` で更新する `appAuthPresentation` に追随（別タブの storage では更新しない） */
const clearLoggedInPresentation = (): void => {
  computedIsLoggedIn.value = false;
  computedUserName.value = '';
  computedPlatform.value = '';
  isResumeGeneratorAvailable.value = false;
  considerationBadgeCount.value = undefined;
  scoutBadgeCount.value = undefined;
  selectionBadgeCount.value = undefined;
  isLoading.value = false;
};

watch(
  () => appAuthPresentation.value,
  (authenticated) => {
    if (authenticated === true) {
      computedIsLoggedIn.value = true;
      isLoading.value = true;
      void tryFetchProfile();
      void tryFetchCareerDocumentAvailability();
    } else if (authenticated === false) {
      clearLoggedInPresentation();
    } else {
      /** `null`: 確定前はナビを出さない（SSR の data 属性とズレた中間表示を避ける） */
      isLoading.value = true;
    }
  },
  { immediate: true, flush: 'post' }
);

const navItemsWithBadge = computed(() =>
  navItems.map((item) => {
    if (item.label === '気になる/検討中') {
      return { ...item, badgeCount: considerationBadgeCount.value };
    }
    if (item.label === 'スカウト/おすすめ') {
      return { ...item, badgeCount: scoutBadgeCount.value };
    }
    if (item.label === '選考管理') {
      return { ...item, badgeCount: selectionBadgeCount.value };
    }
    return item;
  })
);

const settingsMenuItemsWithResumeGenerator = computed(() => {
  if (!isResumeGeneratorAvailable.value) return settingsMenuItems;
  const resumeGeneratorItem = {
    label: '職務経歴書作成',
    href: '/resume-generator',
  } as const;
  return [...settingsMenuItems.slice(0, 3), resumeGeneratorItem, ...settingsMenuItems.slice(3)];
});

const handleLogout = () => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = '/logout';
  const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
  if (token) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'authenticity_token';
    input.value = token;
    form.appendChild(input);
  }
  const method = document.createElement('input');
  method.type = 'hidden';
  method.name = '_method';
  method.value = 'delete';
  form.appendChild(method);
  document.body.appendChild(form);
  form.submit();
};

let removeAfterEach: (() => void) | undefined;

const onHeaderBadgeRefreshEvent = (e: Event): void => {
  if (appAuthPresentation.value !== true && !computedIsLoggedIn.value) return;
  const ce = e as CustomEvent<HeaderBadgeRefreshDetail | undefined>;
  const detail = ce.detail;
  if (detail && applyHeaderBadgesFromDetail(detail)) {
    return;
  }
  void tryFetchProfile();
};

const onResumeGeneratorAvailabilityRefresh = (): void => {
  if (appAuthPresentation.value !== true && !computedIsLoggedIn.value) return;
  void tryFetchCareerDocumentAvailability();
};

const shouldRefreshResumeGeneratorAvailability = (path: string): boolean =>
  path.startsWith('/resume-generator');

onMounted(() => {
  updateIsSp();
  runDetectActive();
  window.addEventListener('resize', updateIsSp);
  window.addEventListener(HEADER_BADGE_REFRESH_EVENT, onHeaderBadgeRefreshEvent);
  window.addEventListener(
    RESUME_GENERATOR_AVAILABILITY_REFRESH_EVENT,
    onResumeGeneratorAvailabilityRefresh
  );
  removeAfterEach = router.afterEach((to, from) => {
    runDetectActive();
    if (
      shouldRefreshResumeGeneratorAvailability(to.path) ||
      shouldRefreshResumeGeneratorAvailability(from.path)
    ) {
      requestResumeGeneratorAvailabilityRefresh();
    }
  });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateIsSp);
  window.removeEventListener(HEADER_BADGE_REFRESH_EVENT, onHeaderBadgeRefreshEvent);
  window.removeEventListener(
    RESUME_GENERATOR_AVAILABILITY_REFRESH_EVENT,
    onResumeGeneratorAvailabilityRefresh
  );
  removeAfterEach?.();
});
</script>

<style scoped>
.global-header {
  width: 100%;
}
</style>
